import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Article as ArticleIcon,
  Launch as LaunchIcon,
  Psychology as PsychologyIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import publicationsData from '../data/publications.json';

interface Publication {
  title: string;
  subtitle: string;
  description: string;
  count: string;
  icon: React.ReactNode;
  link: string;
  recentArticles: string[];
  category: string;
}

const PublicationsSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  // Icon mapping function
  const getIconComponent = (iconName: string): React.ReactNode => {
    const iconMap: { [key: string]: React.ReactNode } = {
      School: <SchoolIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      Psychology: <PsychologyIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
    };
    return iconMap[iconName] || <ArticleIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />;
  };

  // Process JSON data to include icon components
  const publications: Publication[] = publicationsData.publications.map(item => ({
    ...item,
    icon: getIconComponent(item.icon),
  }));

  const cardBg = isDark
    ? 'linear-gradient(135deg, rgba(26,26,34,0.98) 0%, rgba(34,34,44,0.98) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)';
  const cardBorder = isDark
    ? '1px solid rgba(144,202,249,0.08)'
    : '1px solid rgba(26, 115, 232, 0.08)';

  return (
    <Box
      id="publications"
      sx={{
        py: { xs: 4, sm: 6, md: 8 },
        backgroundColor: theme.palette.background.default,
        minHeight: { xs: 'auto', md: '100vh' },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: isDark 
            ? 'linear-gradient(135deg, rgba(26, 115, 232, 0.03) 0%, rgba(52, 168, 83, 0.03) 100%)'
            : 'linear-gradient(135deg, rgba(26, 115, 232, 0.01) 0%, rgba(52, 168, 83, 0.01) 100%)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box textAlign="center" mb={{ xs: 4, sm: 5, md: 6 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              className="section-heading-allura"
              sx={{
                fontWeight: 400,
                fontFamily: 'Allura, cursive !important',
                fontSize: { xs: '36px !important', sm: '42px !important', md: '48px !important' },
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #EC4899 100%)'
                  : 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 50%, #DB2777 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '48px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '56px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '68px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '72px !important',
                },
              }}
            >
              Publications
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              className="section-description-allura"
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                fontFamily: 'Allura, cursive !important',
                fontWeight: 400,
                fontSize: '28px !important',
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '32px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '34px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '35px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '36px !important',
                },
              }}
            >
              Sharing knowledge through AI/ML education and leadership insights
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={3} sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {publications.map((publication, index) => (
            <Grid key={index} sx={{ 
              flex: { xs: '0 0 100%', md: '0 0 calc(50% - 12px)' },
              maxWidth: { xs: '100%', md: 'calc(50% - 12px)' }
            }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <Card 
                  sx={{ 
                    height: '100%',
                    background: cardBg,
                    backdropFilter: 'blur(20px)',
                    border: cardBorder,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: isDark ? '0 16px 48px #222' : '0 16px 48px rgba(26, 115, 232, 0.15)',
                      border: isDark ? '1px solid #90caf9' : '1px solid rgba(26, 115, 232, 0.2)',
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                      <Box sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }}>
                        {publication.icon}
                      </Box>
                      <Box sx={{ ml: { xs: 1.5, md: 2 }, flexGrow: 1 }}>
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{ 
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                            mb: { xs: 0.25, md: 0.5 },
                            fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.1rem' },
                            lineHeight: { xs: 1.3, md: 1.4 },
                            '@media (min-width:601px) and (max-width:960px)': {
                              fontSize: '1.0625rem',
                              lineHeight: 1.45,
                            },
                          }}
                        >
                          {publication.title}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ 
                            fontWeight: 500,
                            mb: { xs: 0.75, md: 1 },
                            fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.9rem' },
                            lineHeight: { xs: 1.4, md: 1.5 },
                            '@media (min-width:601px) and (max-width:960px)': {
                              fontSize: '0.84375rem',
                              lineHeight: 1.53,
                            },
                          }}
                        >
                          {publication.subtitle}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={publication.count}
                            size="small"
                            sx={{
                              backgroundColor: theme.palette.primary.light,
                              color: theme.palette.primary.contrastText,
                              fontWeight: 600,
                              fontSize: isMobile ? '0.625rem' : '0.7rem',
                              height: isMobile ? 18 : 20,
                              '@media (min-width:601px) and (max-width:960px)': {
                                fontSize: '0.65625rem',
                                height: 19,
                              },
                            }}
                          />
                          <Chip
                            label={publication.category}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                              color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.main,
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
                              fontWeight: 500,
                              fontSize: isMobile ? '0.625rem' : '0.7rem',
                              height: isMobile ? 18 : 20,
                              '@media (min-width:601px) and (max-width:960px)': {
                                fontSize: '0.65625rem',
                                height: 19,
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                    
                    <Typography
                      variant="body2"
                      sx={{ 
                        mb: 3, 
                        color: theme.palette.text.secondary,
                        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                        lineHeight: { xs: 1.5, md: 1.6 },
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 2, md: 3 },
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        '@media (min-width:601px) and (max-width:960px)': {
                          fontSize: '0.84375rem',
                          lineHeight: 1.55,
                        },
                      }}
                    >
                      {publication.description}
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        component="h4"
                        sx={{ 
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          mb: 1.5,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.9rem' },
                          '@media (min-width:601px) and (max-width:960px)': {
                            fontSize: '0.84375rem',
                            lineHeight: 1.53,
                          },
                        }}
                      >
                        Recent Articles:
                      </Typography>
                      <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                        {publication.recentArticles.slice(0, 3).map((article, articleIndex) => (
                          <Typography
                            key={articleIndex}
                            variant="body2"
                            sx={{
                              mb: 0.5,
                              color: theme.palette.text.secondary,
                              fontSize: isMobile ? '0.6875rem' : '0.75rem',
                              lineHeight: 1.4,
                              '@media (min-width:601px) and (max-width:960px)': {
                                fontSize: '0.71875rem',
                                lineHeight: 1.45,
                              },
                              '&:hover': {
                                color: theme.palette.primary.main,
                              },
                            }}
                          >
                            • {article}
                          </Typography>
                        ))}
                        {publication.recentArticles.length > 3 && (
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ 
                              fontWeight: 500,
                              fontStyle: 'italic',
                              fontSize: isMobile ? '0.6875rem' : '0.75rem',
                              '@media (min-width:601px) and (max-width:960px)': {
                                fontSize: '0.71875rem',
                                lineHeight: 1.45,
                              },
                            }}
                          >
                            +{publication.recentArticles.length - 3} more articles...
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
                      href={publication.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontWeight: 600,
                        px: { xs: 1, md: 2 },
                        py: 1,
                        borderRadius: 1.5,
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                        color: theme.palette.mode === 'dark' ? '#000' : '#fff',
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #000000 0%, #333333 50%, #000000 100%)'
                          : 'linear-gradient(135deg, #000000 0%, #333333 50%, #000000 100%)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 24px 0 rgba(0, 0, 0, 0.5)'
                          : '0 4px 24px 0 rgba(0, 0, 0, 0.2)',
                        border: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px) scale(1.05)',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 32px 0 rgba(0, 0, 0, 0.6)'
                            : '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
                        },
                        '& .MuiButton-endIcon': {
                          marginLeft: { xs: 0, md: 0.5 },
                        },
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        Read on Medium
                      </Box>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PublicationsSection; 